import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { ReactNode } from 'react';
import { useMemo, useRef } from 'react';

import { ScrollShadowBox } from '@adflow/frontend/ui-layout';
import { su, theme } from '@adflow/frontend/ui-theme';
import { useRect, useWindowViewHeight } from '@adflow/frontend/util-common';

import type { ModalProps } from './Modal';
import { Modal } from './Modal';

const styles = {
	modalDialogOverride: {
		padding: '1.25rem',
		backgroundColor: theme.colors.background.form,
		boxShadow: '0px 4px 16px 0px gray',
	},
	modalHeader: css(su.text2xl, {
		color: theme.colors.text.primary,
		flex: '1 1 auto',
		paddingLeft: '1.25rem',
		paddingRight: '1.25rem',
		paddingTop: '0.5rem',
		paddingBottom: '0.5rem',
		margin: '0px',
		fontWeight: '700',
	}),
	modalBody: css({ flex: '1 1 auto', paddingBottom: '0.75rem', margin: '0px' }),
	modalFooter: css({
		paddingLeft: '1.25rem',
		paddingRight: '1.25rem',
		paddingTop: '0.5rem',
		paddingBottom: '0.5rem',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flex: '1 1 auto',
		margin: '0px',

		'& > :not(:first-of-type)': css({ marginLeft: '1.25rem' }),
	}),
};

interface ModalWithLayoutContentProps {
	header: ReactNode;
	headerStyle?: CSSInterpolation;
	body: ReactNode;
	bodyStyle?: CSSInterpolation;
	footer: ReactNode;
	footerStyle?: CSSInterpolation;
	noScrollAreaPadding?: boolean;
	leftOffset?: number;
	topOffset?: number;
}

function ModalWithLayoutContent(props: ModalWithLayoutContentProps) {
	const {
		header,
		headerStyle,
		body,
		bodyStyle,
		footer,
		footerStyle,
		noScrollAreaPadding,
		leftOffset,
		topOffset,
	} = props;
	const windowHeight = useWindowViewHeight();

	const containerRef = useRef<HTMLDivElement>(null);
	const containerHeight = useRect(containerRef)?.height;
	const headerRef = useRef<HTMLDivElement>(null);
	const headerHeight = useRect(headerRef)?.height;
	const footerRef = useRef<HTMLDivElement>(null);
	const footerHeight = useRect(footerRef)?.height;

	const bodyHeightStyle = useMemo(() => {
		if (
			containerHeight === undefined ||
			headerHeight === undefined ||
			footerHeight === undefined
		) {
			return;
		}

		const limit = windowHeight - 40;

		// Sometimes the container height is 1px less than the limit, this
		// causes a infinite loop resizing the container. So we add 1px
		// and round up the container height to prevent this.
		if (Math.ceil(containerHeight) + 1 >= limit) {
			return `${limit - headerHeight - footerHeight}px`;
		}

		return 'auto';
	}, [containerHeight, footerHeight, headerHeight, windowHeight]);

	return (
		<div ref={containerRef} data-testid="modal-layout-content">
			<div css={[styles.modalHeader, headerStyle]} ref={headerRef}>
				{header}
			</div>
			<ScrollShadowBox
				height={bodyHeightStyle}
				scrollAreaStyle={[
					noScrollAreaPadding
						? undefined
						: { paddingLeft: '1.25rem', paddingRight: '1.25rem' },
					bodyStyle,
				]}
				leftOffset={leftOffset ?? 0}
				topOffset={topOffset ?? 0}
			>
				{body}
			</ScrollShadowBox>
			<div
				css={footer ? [styles.modalFooter, footerStyle] : undefined}
				ref={footerRef}
				data-testid="modal-footer"
			>
				{footer}
			</div>
		</div>
	);
}

interface ModalWithLayoutProps
	extends Omit<ModalProps, 'children'>, ModalWithLayoutContentProps {}

export function ModalWithLayout(props: ModalWithLayoutProps) {
	const {
		header,
		headerStyle,
		body,
		bodyStyle,
		footer,
		footerStyle,
		noScrollAreaPadding,
		leftOffset,
		topOffset,
		...modalProps
	} = props;

	return (
		<Modal
			{...modalProps}
			dialogStyleOverrides={[modalProps.dialogStyleOverrides, { padding: '0' }]}
		>
			<ModalWithLayoutContent
				header={header}
				headerStyle={headerStyle}
				body={body}
				bodyStyle={bodyStyle}
				footer={footer}
				footerStyle={footerStyle}
				noScrollAreaPadding={noScrollAreaPadding}
				leftOffset={leftOffset}
				topOffset={topOffset}
			/>
		</Modal>
	);
}
